import { useCallback, useEffect, useState } from 'react'
import { Desktop, DownloadSimple, X } from '@phosphor-icons/react'

import { Button } from '@/components/ui/button'
import {
  canShowPwaUi,
  capturePwaInstallPromptEarly,
  clearDeferredPwaInstallPrompt,
  dismissPwaInstallBanner,
  isDesktopBrowser,
  isPwaInstallDismissed,
  isPwaNativeOrInstalled,
  PWA_INSTALL_DISMISS_DAYS,
  subscribePwaInstallPrompt,
} from '@/lib/pwa-utils'

const INSTALL_DELAY_MS = 30_000

export function PwaShell() {
  const [installDelayElapsed, setInstallDelayElapsed] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(() =>
    isPwaInstallDismissed(),
  )
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null)
  const [installing, setInstalling] = useState(false)

  const dismissInstallBanner = useCallback(() => {
    dismissPwaInstallBanner()
    setBannerDismissed(true)
  }, [])

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt || installing) return

    setInstalling(true)
    try {
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      clearDeferredPwaInstallPrompt()
      setDeferredPrompt(null)

      if (outcome === 'accepted') {
        dismissInstallBanner()
      }
    } catch {
      // prompt pode falhar se o navegador invalidar o evento
      clearDeferredPwaInstallPrompt()
      setDeferredPrompt(null)
    } finally {
      setInstalling(false)
    }
  }, [deferredPrompt, dismissInstallBanner, installing])

  useEffect(() => {
    capturePwaInstallPromptEarly()
    return subscribePwaInstallPrompt(setDeferredPrompt)
  }, [])

  useEffect(() => {
    if (!canShowPwaUi() || bannerDismissed) return

    let timeoutId: number | undefined

    const scheduleDelay = () => {
      timeoutId = window.setTimeout(() => {
        setInstallDelayElapsed(true)
      }, INSTALL_DELAY_MS)
    }

    if (navigator.onLine) {
      scheduleDelay()
    } else {
      const onOnline = () => {
        window.removeEventListener('online', onOnline)
        scheduleDelay()
      }
      window.addEventListener('online', onOnline)
      return () => {
        window.removeEventListener('online', onOnline)
        if (timeoutId !== undefined) window.clearTimeout(timeoutId)
      }
    }

    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId)
    }
  }, [bannerDismissed])

  const showInstallBannerVisible =
    installDelayElapsed &&
    !bannerDismissed &&
    !isPwaNativeOrInstalled() &&
    isDesktopBrowser() &&
    Boolean(deferredPrompt)

  if (!showInstallBannerVisible) {
    return null
  }

  return (
    <div
      role="region"
      aria-label="Instalar aplicativo"
      className="fixed inset-x-0 bottom-0 z-[55] border-t border-indigo-200 bg-indigo-600 px-4 py-3 text-white shadow-lg pb-[max(0.75rem,env(safe-area-inset-bottom))] md:bottom-4 md:mx-auto md:max-w-xl md:rounded-xl md:border md:pb-3"
    >
      <div className="flex items-center gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white/15">
          <Desktop className="size-5" weight="duotone" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">
            Instale o ImobDesk no computador
          </p>
          <p className="mt-0.5 text-xs text-indigo-100">
            Tenha o CRM na barra de tarefas ou na área de trabalho, com acesso
            rápido e sem abrir o navegador.
          </p>
        </div>

        <Button
          type="button"
          size="sm"
          disabled={installing}
          className="h-8 shrink-0 bg-white px-3 text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
          onClick={() => void handleInstall()}
        >
          <DownloadSimple className="size-4" />
          {installing ? 'Abrindo…' : 'Instalar'}
        </Button>

        <button
          type="button"
          className="rounded-md p-1 text-indigo-100 transition-colors hover:bg-white/10 hover:text-white"
          aria-label={`Ocultar aviso de instalação por ${PWA_INSTALL_DISMISS_DAYS} dias`}
          title={`Ocultar por ${PWA_INSTALL_DISMISS_DAYS} dias`}
          onClick={dismissInstallBanner}
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  )
}
